import React, { useState, useEffect, useReducer, useContext } from "react";
import openSocket from "../../services/socket-io";

import { makeStyles } from "@material-ui/core/styles";
import List from "@material-ui/core/List";
import Paper from "@material-ui/core/Paper";

import InternalChatListItem from "../InternalChatListItem";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  ticketsListWrapper: {
    position: "relative",
    display: "flex",
    height: "100%",
    flexDirection: "column",
    overflow: "hidden",
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
  },
  ticketsList: {
    flex: 1,
    overflowY: "scroll",
    ...theme.scrollbarStyles,
    borderTop: "2px solid rgba(0, 0, 0, 0.12)",
  },
  noTicketsDiv: {
    display: "flex",
    height: "100px",
    margin: 40,
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  noTicketsText: {
    textAlign: "center",
    color: "rgb(104, 121, 146)",
    fontSize: "14px",
    lineHeight: "1.4",
  },
  noTicketsTitle: {
    textAlign: "center",
    fontSize: "16px",
    fontWeight: "600",
    margin: "0px",
  },
}));

const reducer = (state, action) => {
  if (action.type === "LOAD_CHATS") {
    return action.payload;
  }
  if (action.type === "UPDATE_CHAT") {
    const chat = action.payload;
    const index = state.findIndex((c) => c.id === chat.id);
    if (index !== -1) {
      const next = [...state];
      next[index] = { ...next[index], ...chat };
      return [next[index], ...next.filter((_, i) => i !== index)];
    }
    return [chat, ...state];
  }
  if (action.type === "RESET") {
    return [];
  }
  return state;
};

const filterChatsBySearch = (chats, searchParam, user) => {
  if (!searchParam.trim()) return chats;
  const term = searchParam.toLowerCase();
  return chats.filter((chat) => {
    if (chat.isGroup && chat.name && chat.name.toLowerCase().includes(term))
      return true;
    if (chat.users && chat.users.length > 0) {
      const other = chat.users.find((u) => u.id !== user?.id) || chat.users[0];
      if (other.name && other.name.toLowerCase().includes(term)) return true;
    }
    if (chat.lastMessage && chat.lastMessage.toLowerCase().includes(term))
      return true;
    return false;
  });
};

const InternalChatsList = ({ searchParam = "", refreshTrigger, style }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const [chatsList, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(true);

  const filteredChats = React.useMemo(() => {
    const chats = filterChatsBySearch(chatsList, searchParam, user);
    return [...chats].sort(
      (a, b) =>
        new Date(b.updatedAt || 0).getTime() -
        new Date(a.updatedAt || 0).getTime()
    );
  }, [chatsList, searchParam, user]);

  const fetchChats = async () => {
    try {
      const { data } = await api.get("/internal-chats");
      dispatch({ type: "LOAD_CHATS", payload: data.chats || [] });
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChats();
  }, []);

  useEffect(() => {
    if (refreshTrigger > 0) fetchChats();
  }, [refreshTrigger]);

  useEffect(() => {
    const socket = openSocket();
    socket.on("connect", () => socket.emit("joinInternalNotification"));
    socket.on("internalChat", (data) => {
      if (data.action === "update" && data.chat) {
        dispatch({ type: "UPDATE_CHAT", payload: data.chat });
      }
    });
    return () => socket.disconnect();
  }, []);

  return (
    <Paper className={classes.ticketsListWrapper} style={style}>
      <Paper square name="internalChats" elevation={0} className={classes.ticketsList}>
        <List style={{ paddingTop: 0 }}>
          {filteredChats.length === 0 && !loading ? (
            <div className={classes.noTicketsDiv}>
              <span className={classes.noTicketsTitle}>
                {i18n.t("internalChat.noChatsTitle")}
              </span>
              <p className={classes.noTicketsText}>
                {i18n.t("internalChat.noChatsMessage")}
              </p>
            </div>
          ) : (
            filteredChats.map((chat) => (
              <InternalChatListItem chat={chat} user={user} key={chat.id} />
            ))
          )}
        </List>
      </Paper>
    </Paper>
  );
};

export default InternalChatsList;
